import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import {BrowserRouter} from 'react-router-dom'
import './bootstrap.min-tap.css'
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import axios from 'axios';
//  The "do you want to install this?" dialog. Self-contained: it decides for
//  itself whether to appear, and stays silent once answered.
import InstallPrompt from './Components/InstallPrompt';

// ── Audit interceptors ──────────────────────────────────────────────────────
// Every outgoing request gets stamped with X-Actor (the logged-in username)
// so the backend audit middleware can record WHO acted, regardless of what
// the request body contains. Applies to both axios and fetch so call sites
// don't need to change.
const _currentActor = () => {
  try {
    const raw = localStorage.getItem('userInfo');
    if (!raw) return '';
    const u = JSON.parse(raw);
    return u?.username || u?.email || u?.name || '';
  } catch (_) { return ''; }
};

// Pull the active branch id from the logged-in userInfo. Backend reads it
// from x-branch-id and scopes inserts/queries to that branch. Defaults to
// null when the user hasn't logged in yet so the request is treated as
// pre-auth (login itself doesn't need the header).
const _currentBranchId = () => {
  try {
    const raw = localStorage.getItem('userInfo');
    if (!raw) return null;
    const u = JSON.parse(raw);
    return u?.branch?.id || null;
  } catch (_) { return null; }
};

axios.interceptors.request.use((config) => {
  const actor = _currentActor();
  if (actor) {
    config.headers = config.headers || {};
    config.headers['X-Actor'] = actor;
  }
  const bId = _currentBranchId();
  if (bId) {
    config.headers = config.headers || {};
    config.headers['X-Branch-Id'] = String(bId);
  }
  return config;
});

if (typeof window !== 'undefined' && typeof window.fetch === 'function' && !window.__auditFetchPatched) {
  const _origFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    try {
      const actor = _currentActor();
      const bId   = _currentBranchId();
      if (actor || bId) {
        const headers = new Headers(init.headers || (input && input.headers) || {});
        if (actor && !headers.has('X-Actor'))     headers.set('X-Actor', actor);
        if (bId   && !headers.has('X-Branch-Id')) headers.set('X-Branch-Id', String(bId));
        init = { ...init, headers };
      }
    } catch (_) { /* fall through to original fetch */ }
    return _origFetch(input, init);
  };
  window.__auditFetchPatched = true;
}
// ────────────────────────────────────────────────────────────────────────────

const theme = createTheme({
  palette: {
    primary: {
     main: '#10b981',
      light: '#10b981',
      dark: '#10b981',
    },
    background: {
      default: '#f5f5f5',
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(

  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <App />
        {/*  Offers to install TotalCare the first time it is opened in a browser
             that supports it, then remembers the answer. Outside <App> so it is
             not tied to any route and cannot be unmounted by navigation. */}
        <InstallPrompt />
      </BrowserRouter>
    </ThemeProvider>
  </React.StrictMode>
);

/* ── INSTALLABILITY ──────────────────────────────────────────────────────────
 *
 *  A browser only offers to install an app that has a registered service worker
 *  with a fetch handler, served over HTTPS. public/service-worker.js is that,
 *  and NOTHING MORE — it caches nothing at all. See the long note at the top of
 *  that file: on a live clinical system a cached response is a stale drug chart
 *  or a stale claim, and that is not a trade worth making for offline polish.
 *
 *  PRODUCTION ONLY. Under `npm start` the dev server already serves fresh files
 *  and a worker in the mix only adds a layer to be confused by.
 *
 *  Registration is deliberately unawaited and its failure swallowed: if it does
 *  not register, the app is simply not installable, which must never stop it
 *  from loading. */
if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(`${process.env.PUBLIC_URL || ''}/service-worker.js`)
      .catch((err) => {
        console.warn('[TotalCare] service worker not registered:', err && err.message);
      });
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
